import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/i18nContext';
import Button from '../../components/ui/Button';
import '../../styles/AdminEntity.css';

// ============================================================
// Backup — teljes rendszer-mentés (DB + uploads + .env) jelszóval
// védett ZIP-be, GFS (napi/heti/havi) rotációval. Kizárólag
// superadmin (route-szinten is védve). Az éjszakai cron ugyanezt a
// logikát hívja (backend/scripts/generateBackup.js), ez az oldal a
// meglévő csomagok listázására, letöltésére és azonnali generálásra való.
// ============================================================

const CATEGORIES = ['daily', 'weekly', 'monthly'];
const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'];

const fmtSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
};
const fmtDate = (iso) => new Date(iso).toLocaleString([], {
  year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
});

// A tárolt ütemezés UTC-ben van (lásd backend/scripts/generateBackup.js) —
// ez itt csak a néző saját böngésző-időzónájában mutatja meg a megfelelőt,
// a háttérlogikát nem érinti.
const localTimePreview = (hh, mm) => {
  const now = new Date();
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), Number(hh), Number(mm)));
  return new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' }).format(d);
};

const BackupsPage = () => {
  const { api } = useAuth();
  const { t } = useI18n();

  const [passwordSet, setPasswordSet] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  const [scheduleHour, setScheduleHour] = useState('03');
  const [scheduleMinute, setScheduleMinute] = useState('30');
  const [savingSchedule, setSavingSchedule] = useState(false);

  const [retention, setRetention] = useState({ daily: 14, weekly: 8, monthly: 12 });
  const [savingRetention, setSavingRetention] = useState(false);

  const [diskUsage, setDiskUsage] = useState(null);

  const [backups, setBackups] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [msg, setMsg] = useState(null);

  const flash = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 5000);
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, list, schedule, ret, disk] = await Promise.all([
        api.get('/admin/backups/config'),
        api.get('/admin/backups'),
        api.get('/admin/backups/schedule'),
        api.get('/admin/backups/retention'),
        api.get('/admin/backups/disk-usage'),
      ]);
      setPasswordSet(cfg.data.password_set);
      setBackups(list.data);
      const [h, m] = (schedule.data.time || '03:30').split(':');
      setScheduleHour(h);
      setScheduleMinute(m);
      setRetention(ret.data);
      setDiskUsage(disk.data);
    } catch {
      flash(t('backups.loadFailed'), 'error');
    } finally {
      setLoading(false);
    }
  }, [api, t]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const savePassword = async () => {
    if (newPassword.length < 8) {
      flash(t('backups.passwordTooShort'), 'error');
      return;
    }
    setSavingPw(true);
    try {
      await api.put('/admin/backups/config', { password: newPassword });
      setNewPassword('');
      setPasswordSet(true);
      flash(t('backups.passwordSaved'));
    } catch {
      flash(t('backups.passwordSaveFailed'), 'error');
    } finally {
      setSavingPw(false);
    }
  };

  const saveSchedule = async () => {
    setSavingSchedule(true);
    try {
      await api.put('/admin/backups/schedule', { time: `${scheduleHour}:${scheduleMinute}` });
      flash(t('backups.scheduleSaved'));
    } catch {
      flash(t('backups.scheduleSaveFailed'), 'error');
    } finally {
      setSavingSchedule(false);
    }
  };

  const saveRetention = async () => {
    setSavingRetention(true);
    try {
      await api.put('/admin/backups/retention', retention);
      flash(t('backups.retentionSaved'));
    } catch (e) {
      flash(e.response?.data?.error || t('backups.retentionSaveFailed'), 'error');
    } finally {
      setSavingRetention(false);
    }
  };

  const generateNow = async () => {
    setGenerating(true);
    try {
      const r = await api.post('/admin/backups/generate');
      flash(`${t('backups.generated')}: ${r.data.filename} (${fmtSize(r.data.size)})`);
      await loadAll();
    } catch (e) {
      flash(e.response?.data?.error || t('backups.generateFailed'), 'error');
    } finally {
      setGenerating(false);
    }
  };

  const download = async (category, filename) => {
    try {
      const r = await api.get(`/admin/backups/${category}/${filename}/download`, { responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(r.data);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      flash(t('backups.downloadFailed'), 'error');
    }
  };

  const deleteOne = async (category, filename) => {
    if (!window.confirm(t('backups.deleteConfirm').replace('{filename}', filename))) return;
    try {
      await api.delete(`/admin/backups/${category}/${filename}`);
      flash(t('backups.deleted'));
      await loadAll();
    } catch {
      flash(t('backups.deleteFailed'), 'error');
    }
  };

  return (
    <div className="ae-page">
      {msg && <div className={`ae-msg ${msg.type}`}>{msg.text}</div>}

      <div className="ae-card ae-header-card">
        <div className="ae-topbar">
          <h1 className="ae-title">💾 {t('backups.title')}</h1>
          <Button variant="ghost" size="sm" onClick={loadAll}>↻ {t('testlab.refresh')}</Button>
        </div>
        <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.85rem' }}>{t('backups.intro')}</p>
        {diskUsage && (() => {
          const pctUsed = Math.round((diskUsage.usedBytes / diskUsage.totalBytes) * 100);
          const low = pctUsed >= 90;
          return (
            <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: low ? 'var(--error-text)' : 'var(--text-muted)' }}>
              💽 {t('backups.diskFree')
                .replace('{free}', fmtSize(diskUsage.freeBytes))
                .replace('{total}', fmtSize(diskUsage.totalBytes))
                .replace('{percent}', pctUsed)}
            </p>
          );
        })()}
      </div>

      {/* JELSZÓ */}
      <div className="ae-card testlab-card">
        <p className="ae-form-section-title">{t('backups.passwordTitle')}</p>
        {passwordSet !== null && (
          <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem' }}>
            <span className={`ae-badge ${passwordSet ? 'active' : 'inactive'}`}>
              {passwordSet ? t('backups.passwordIsSet') : t('backups.passwordNotSet')}
            </span>
          </p>
        )}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <div className="pw-input-wrap">
            <input type={showPassword ? 'text' : 'password'} value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder={t('backups.newPasswordPlaceholder')} style={{ minWidth: 220 }} />
            <button type="button" className="pw-eye" tabIndex={-1}
              onClick={() => setShowPassword(s => !s)}
              aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}>
              {showPassword ? '🙈' : '👁️'}
            </button>
          </div>
          <Button variant="solid" size="sm" disabled={savingPw || !newPassword} onClick={savePassword}>
            {savingPw ? t('testlab.running') : t('backups.savePassword')}
          </Button>
        </div>
        <small style={{ display: 'block', marginTop: '0.5rem', color: 'var(--text-muted)' }}>
          {t('backups.passwordHint')}
        </small>
      </div>

      {/* GENERÁLÁS */}
      <div className="ae-card testlab-card">
        <p className="ae-form-section-title">{t('backups.generateTitle')}</p>
        <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
          {t('backups.generateHint')}
        </p>
        <Button variant="solid" disabled={generating || !passwordSet} onClick={generateNow}>
          {generating ? t('testlab.running') : `📦 ${t('backups.generateNow')}`}
        </Button>
        {!passwordSet && (
          <small style={{ display: 'block', marginTop: '0.5rem', color: 'var(--error-text)' }}>
            {t('backups.needPasswordFirst')}
          </small>
        )}
      </div>

      {/* ÜTEMEZÉS */}
      <div className="ae-card testlab-card">
        <p className="ae-form-section-title">{t('backups.scheduleTitle')}</p>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <select className="ae-select" value={scheduleHour} onChange={e => setScheduleHour(e.target.value)}>
            {HOURS.map(h => <option key={h} value={h}>{h}</option>)}
          </select>
          <span style={{ color: 'var(--text-muted)' }}>:</span>
          <select className="ae-select" value={scheduleMinute} onChange={e => setScheduleMinute(e.target.value)}>
            {MINUTES.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>UTC</span>
          <Button variant="solid" size="sm" disabled={savingSchedule} onClick={saveSchedule}>
            {savingSchedule ? t('testlab.running') : t('backups.savePassword')}
          </Button>
        </div>
        <p style={{ margin: '0.5rem 0 0', fontSize: '0.85rem', color: 'var(--text-primary)' }}>
          🕒 {t('backups.scheduleLocalPreview').replace('{local}', localTimePreview(scheduleHour, scheduleMinute))}
        </p>
        <small style={{ display: 'block', marginTop: '0.35rem', color: 'var(--text-muted)' }}>
          {t('backups.scheduleHint')}
        </small>
      </div>

      {/* MEGŐRZÉS (GFS retention) */}
      <div className="ae-card testlab-card">
        <p className="ae-form-section-title">{t('backups.retentionTitle')}</p>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {CATEGORIES.map(category => (
            <div className="ae-field" key={category} style={{ width: 120 }}>
              <label>{t(`backups.categories.${category}`)}</label>
              <input type="number" min={1} max={365} value={retention[category]}
                onChange={e => setRetention(r => ({ ...r, [category]: Number(e.target.value) }))} />
            </div>
          ))}
          <Button variant="solid" size="sm" disabled={savingRetention} onClick={saveRetention}>
            {savingRetention ? t('testlab.running') : t('backups.savePassword')}
          </Button>
        </div>
        <small style={{ display: 'block', marginTop: '0.5rem', color: 'var(--text-muted)' }}>
          {t('backups.retentionHint')}
        </small>
      </div>

      {/* LISTÁK */}
      {CATEGORIES.map(category => (
        <div key={category} className="ae-card testlab-card">
          <p className="ae-form-section-title">{t(`backups.categories.${category}`)}</p>
          {loading ? (
            <div className="ae-empty">{t('common.loading')}</div>
          ) : !backups?.[category]?.length ? (
            <div className="ae-empty">{t('backups.none')}</div>
          ) : (
            <div className="ae-table-wrap">
              <table className="ae-table">
                <thead>
                  <tr>
                    <th>{t('backups.filename')}</th>
                    <th>{t('backups.size')}</th>
                    <th>{t('backups.createdAt')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {backups[category].map(b => (
                    <tr key={b.filename}>
                      <td className="ae-name">{b.filename}</td>
                      <td>{fmtSize(b.size)}</td>
                      <td>{fmtDate(b.created_at)}</td>
                      <td className="ae-actions">
                        <Button variant="ghost" size="sm" onClick={() => download(category, b.filename)}>
                          ⬇ {t('backups.download')}
                        </Button>
                        <Button variant="danger" size="sm" onClick={() => deleteOne(category, b.filename)}>
                          🗑 {t('backups.delete')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}

      {/* KÜLSŐ MÁSOLAT */}
      <div className="ae-card testlab-card">
        <p className="ae-form-section-title">{t('backups.remoteTitle')}</p>
        <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-muted)' }}>
          {t('backups.remoteHint')}
        </p>
      </div>

      {/* RESTORE — csak tájékoztató, nincs gomb: szándékosan CLI/SSH-only */}
      <div className="ae-card testlab-card">
        <p className="ae-form-section-title">{t('backups.restoreTitle')}</p>
        <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
          {t('backups.restoreIntro')}
        </p>
        <code style={{
          display: 'block', padding: '0.6rem 0.9rem', marginBottom: '0.85rem',
          background: 'var(--bg-surface-2)', border: '1px solid var(--border-color)',
          fontSize: '0.8rem', overflowX: 'auto', whiteSpace: 'pre',
        }}>
          node scripts/restoreBackup.js backups/daily/&lt;filename&gt;.zip
        </code>
        <ol style={{ margin: '0 0 1rem', paddingLeft: '1.2rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
          <li>{t('backups.restoreStep1')}</li>
          <li>{t('backups.restoreStep2')}</li>
          <li>{t('backups.restoreStep3')}</li>
          <li>{t('backups.restoreStep4')}</li>
          <li>{t('backups.restoreStep5')}</li>
          <li>{t('backups.restoreStep6')}</li>
          <li>{t('backups.restoreStep7')}</li>
        </ol>
        <div className="ae-msg warning" style={{ marginBottom: 0 }}>
          <strong>{t('backups.restoreSchemaTitle')}</strong>
          <p style={{ margin: '0.4rem 0 0' }}>{t('backups.restoreSchemaText')}</p>
        </div>
      </div>
    </div>
  );
};

export default BackupsPage;
